import {
  AdyenMandatedBy,
  CreateAdyenSessionRequest,
  InvoicePaymentsAdyenSessionData,
  PaymentsAdyenSessionDataView
} from "../../Contracts/AdyenSession";
import {
  AdyenPaymentMethodType,
  AdyenPaymentsResultCodes,
  AdyenStoredPaymentTokenForClientPortalView,
  AdyenTransactionCreateRequest,
  AdyenTransactionCreateView,
  AllowedPaymentMethod,
  BacsResponse,
  GiactValidationRequest,
  GiactValidationView,
  ShowPaymentResultView
} from "../../Contracts/Transaction";
import { create } from "zustand";
import { useRequestStore } from "../BackendStore";
import { useClientPortalMetadataStore } from "../ClientPortalMetadata";
import { ResultCode } from "@adyen/adyen-web/dist/types/components/types";

const clientPortalUrls = {
  createAddPaymentMethodMandate: "/v1/payments/add/paymentMandate/session/create",
  createTransactionToken: "/v1/payments/add/paymentMandate/transactionToken/create/",
  sendMandateCreationNotification: "/v1/payments/add/paymentMandate/sendMandateNotification/",
  giactValidationRequest: "/v1/payments/add/paymentMandate/giactValidation/",
  getAllowedPaymentMethods:
    "/v1/payments/client-portal/customer/{customer_id}/allowedPaymentMethod/",
  getAvailablePaymentMethods:
    "/v1/payments/client-portal/customer/{customer_id}/availablePaymentToken/",
  handleBacsPayment: "/v1/payments/add/paymentMandate/handleBacsPayment/"
};

const publicUrls = {
  createInvoiceCheckoutSession: "/v1/payments/invoice/session/create",
  doGiactValidation: "/v1/payments/invoice/giactValidation",
  createTransactionForInvoice: "/v1/payments/invoice/transaction/create",
  getTransactionCreatResult: "/v1/payments/invoice/transaction-creation-result/{transactionRef}",
  handleBacsPayment: "/v1/payments/invoice/handleBacsPayment"
};

const partnerUrls = {
  createAddPaymentMethodMandate: "/v1/payments/add/partner-paymentMandate/session/create",
  createTransactionToken: "/v1/payments/add/partner-paymentMandate/transactionToken/create",
  handleBacsPayment: "/v1/payments/add/partner-paymentMandate/handleBacsPayment/",
  sendMandateCreationNotification:
    "/v1/payments/add/partner-paymentMandate/sendMandateNotification",
  giactValidationRequest: "/v1/payments/add/partner-paymentMandate/giactValidation/"
};

interface PaymentMethodManagementState {
  isLoading: boolean;
  paymentsAdyenSessionData?: PaymentsAdyenSessionDataView;
  invoiceCheckoutSessionData?: InvoicePaymentsAdyenSessionData;
  adyenTransactionCreateView?: AdyenTransactionCreateView;
  allowedPaymentMethods: Record<string, AllowedPaymentMethod[]>;
  availablePaymentMethods: Record<string, AdyenStoredPaymentTokenForClientPortalView[]>;
  clientPortalFunctions: {
    getAllowedPaymentMethods: (customerId: string) => Promise<void>;
    getAvailablePaymentMethods: (customerId: string) => Promise<void>;
    createAddPaymentMethodMandate: (
      createAdyenSessionRequest: CreateAdyenSessionRequest
    ) => Promise<void>;
    createTransactionToken: (
      reference: string,
      customerId: number,
      paymentMethodType: AdyenPaymentMethodType,
      expiresAt: Date
    ) => Promise<{ reference: string }>;
    sendMandateCreationNotification: (reference: string, resultCode: ResultCode) => Promise<void>;
    doGiactValidation: (
      giactValidationRequest: GiactValidationRequest
    ) => Promise<GiactValidationView>;
    handleBacsPayment: (
      reference: string,
      bacsStateData: any
    ) => Promise<{
      success: boolean;
      status: AdyenPaymentsResultCodes;
      pdf_url?: string;
    }>;
  };
  publicFunctions: {
    createInvoiceCheckoutSession: (
      customer_id: number,
      invoice_id: number,
      payment_method: AdyenPaymentMethodType
    ) => Promise<void>;
    doGiactValidation: (
      giactvalidationRquest: GiactValidationRequest
    ) => Promise<GiactValidationView>;
    createTransactionForInvoice: (
      adyenTransactionCreateRequest: AdyenTransactionCreateRequest
    ) => Promise<AdyenTransactionCreateView>;
    handleBacsPayment: (
      reference: string,
      bacsStateData: any,
      invoiceId: number
    ) => Promise<BacsResponse>;
    getTransactionCreateResult: (
      transactionRef: string,
      resultCode: string
    ) => Promise<ShowPaymentResultView>;
  };
  partnerPortalFunctions: {
    createAddPaymentMethodMandate: (
      getAccessTokenSilently: any,
      createAdyenSessionRequest: CreateAdyenSessionRequest
    ) => Promise<PaymentsAdyenSessionDataView>;
    doGiactValidation: (
      getAccessTokenSilently: any,
      giactValidationRequest: GiactValidationRequest
    ) => Promise<GiactValidationView>;
    createTransactionToken: (
      getAccessTokenSilently: any,
      transcationRef: string,
      customerId: number,
      paymentMethodType: AdyenPaymentMethodType,
      expiresAt: Date
    ) => Promise<{ reference: string }>;
    sendMandateCreationNotification: (
      getAccessTokenSilently: any,
      resultCode: ResultCode,
      transcationRef: string
    ) => Promise<void>;
    handleBacsPayment: (
      getAccessTokenSilently: any,
      reference: string,
      bacsStateData: any
    ) => Promise<BacsResponse>;
  };
}

export const usePaymentMethodManagementStore = create<PaymentMethodManagementState>((set, get) => {
  const { getRequestNoAuth, postRequestNoAuth, postRequest } = useRequestStore.getState();

  const cGetAllowedPaymentMethods = async (customerId: string) => {
    const { clientPortalToken } = useClientPortalMetadataStore.getState();
    set({ isLoading: true });
    const response = await getRequestNoAuth<AllowedPaymentMethod[]>(
      clientPortalUrls.getAllowedPaymentMethods.replace("{customer_id}", customerId),
      { token: clientPortalToken }
    );
    const allowedPaymentMethods = get().allowedPaymentMethods;
    allowedPaymentMethods[customerId] = response;
    set({ allowedPaymentMethods, isLoading: false });
  };

  const cCreateAddPaymentMethodMandate = async (
    createAdyenSessionRequest: CreateAdyenSessionRequest
  ) => {
    set({ isLoading: true });
    const { clientPortalToken } = useClientPortalMetadataStore.getState();
    const response = await postRequestNoAuth<
      CreateAdyenSessionRequest,
      PaymentsAdyenSessionDataView
    >(clientPortalUrls.createAddPaymentMethodMandate, createAdyenSessionRequest, {
      params: { token: clientPortalToken }
    });
    set({ isLoading: false, paymentsAdyenSessionData: response });
  };

  const pCreateAddPaymentMethodMandate = async (
    getAccessTokenSilently: any,
    createAdyenSessionRequest: CreateAdyenSessionRequest
  ): Promise<PaymentsAdyenSessionDataView> => {
    set({ isLoading: true });
    const response = await postRequest<CreateAdyenSessionRequest, PaymentsAdyenSessionDataView>(
      getAccessTokenSilently,
      partnerUrls.createAddPaymentMethodMandate,
      createAdyenSessionRequest
    );
    set({ isLoading: false, paymentsAdyenSessionData: response });
    return response;
  };

  const cCreateTransactionToken = async (
    reference: string,
    customerId: number,
    paymentMethodType: AdyenPaymentMethodType,
    expiresAt: Date
  ): Promise<{ reference: string }> => {
    set({ isLoading: true });
    const { clientPortalToken } = useClientPortalMetadataStore.getState();

    const response = await postRequestNoAuth<
      {
        transaction_ref: string;
        customer_id: number;
        payment_method_type: AdyenPaymentMethodType;
        expires_at: Date;
      },
      { reference: string }
    >(
      clientPortalUrls.createTransactionToken,
      {
        transaction_ref: reference,
        customer_id: customerId,
        payment_method_type: paymentMethodType,
        expires_at: expiresAt
      },
      { params: { token: clientPortalToken } }
    );
    set({ isLoading: false });
    return response;
  };

  const cSendMandateCreationNotification = async (
    reference: string,
    resultCode: ResultCode
  ): Promise<void> => {
    set({ isLoading: true });
    const { clientPortalToken } = useClientPortalMetadataStore.getState();

    await postRequestNoAuth(
      clientPortalUrls.sendMandateCreationNotification,
      {
        result_code: resultCode,
        transaction_ref: reference
      },
      { params: { token: clientPortalToken } }
    );
    set({ isLoading: false });
  };

  const pCreateTransactionToken = async (
    getAccessTokenSilently: any,
    transcationRef: string,
    customerId: number,
    paymentMethodType: AdyenPaymentMethodType,
    expiresAt: Date
  ): Promise<{ reference: string }> => {
    return await postRequest<
      {
        transaction_ref: string;
        customer_id: number;
        payment_method_type: AdyenPaymentMethodType;
        expires_at: Date;
      },
      { reference: string }
    >(getAccessTokenSilently, partnerUrls.createTransactionToken, {
      transaction_ref: transcationRef,
      customer_id: customerId,
      payment_method_type: paymentMethodType,
      expires_at: expiresAt
    });
  };

  const pSendMandateCreationNotification = async (
    getAccessTokenSilently: any,
    resultCode: ResultCode,
    transcationRef: string
  ): Promise<void> => {
    await postRequest(getAccessTokenSilently, partnerUrls.sendMandateCreationNotification, {
      result_code: resultCode,
      transaction_ref: transcationRef
    });
  };

  const pHandleBacsPayment = async (
    getAccessTokenSilently: any,
    reference: string,
    bacsStateData: any
  ): Promise<{
    success: boolean;
    status: AdyenPaymentsResultCodes;
  }> => {
    return await postRequest<
      {
        transaction_ref: string;
        mandated_by: AdyenMandatedBy;
        payment_method_data: any;
      },
      BacsResponse
    >(getAccessTokenSilently, partnerUrls.handleBacsPayment, {
      transaction_ref: reference,
      mandated_by: AdyenMandatedBy.Self,
      payment_method_data: bacsStateData
    });
  };

  const cDoGiactValidation = async (giactValidationRequest: GiactValidationRequest) => {
    set({ isLoading: true });
    const { clientPortalToken } = useClientPortalMetadataStore.getState();

    const response = await postRequestNoAuth<GiactValidationRequest, GiactValidationView>(
      clientPortalUrls.giactValidationRequest,
      giactValidationRequest,
      {
        params: { token: clientPortalToken }
      }
    );
    set({ isLoading: false });
    return response;
  };

  const pDoGiactValidation = async (
    getAccessTokenSilently: any,
    giactValidationRequest: GiactValidationRequest
  ) => {
    set({ isLoading: true });
    const response = await postRequest<GiactValidationRequest, GiactValidationView>(
      getAccessTokenSilently,
      partnerUrls.giactValidationRequest,
      giactValidationRequest
    );
    set({ isLoading: false });
    return response;
  };

  const cGetAvailablePaymentMethods = async (customerId: string) => {
    set({ isLoading: true });
    const { clientPortalToken } = useClientPortalMetadataStore.getState();
    const response = await getRequestNoAuth<AdyenStoredPaymentTokenForClientPortalView[]>(
      clientPortalUrls.getAvailablePaymentMethods.replace("{customer_id}", customerId),
      { token: clientPortalToken }
    );
    const availablePaymentMethods = get().availablePaymentMethods;
    availablePaymentMethods[customerId] = response;
    set({ availablePaymentMethods, isLoading: false });
  };

  const cHandleBacsPayment = async (
    reference: string,
    bacsStateData: any
  ): Promise<{
    success: boolean;
    status: AdyenPaymentsResultCodes;
  }> => {
    set({ isLoading: true });
    const { clientPortalToken } = useClientPortalMetadataStore.getState();

    const response = await postRequestNoAuth<
      {
        transaction_ref: string;
        mandated_by: AdyenMandatedBy;
        payment_method_data: any;
      },
      BacsResponse
    >(
      clientPortalUrls.handleBacsPayment,
      {
        transaction_ref: reference,
        mandated_by: AdyenMandatedBy.Client,
        payment_method_data: bacsStateData
      },
      {
        params: { token: clientPortalToken }
      }
    );
    set({ isLoading: false });
    return response;
  };

  const publicFunctions = {
    createInvoiceCheckoutSession: async (
      customer_id: number,
      invoice_id: number,
      payment_method: AdyenPaymentMethodType
    ) => {
      set({ isLoading: true });
      const response = await postRequestNoAuth<
        {
          method: AdyenPaymentMethodType;
          invoice_id: number;
          customer_id: number;
        },
        InvoicePaymentsAdyenSessionData
      >(publicUrls.createInvoiceCheckoutSession, {
        method: payment_method,
        invoice_id,
        customer_id
      });
      set({ isLoading: false, invoiceCheckoutSessionData: response });
    },
    doGiactValidation: async (giactValidationRequest: GiactValidationRequest) => {
      set({ isLoading: true });
      const response = await postRequestNoAuth<GiactValidationRequest, GiactValidationView>(
        publicUrls.doGiactValidation,
        giactValidationRequest
      );
      set({ isLoading: false });
      return response;
    },
    createTransactionForInvoice: async (
      adyenTransactionForInvoiceCreateRequest: AdyenTransactionCreateRequest
    ) => {
      set({ isLoading: true });
      const response = await postRequestNoAuth<
        AdyenTransactionCreateRequest,
        AdyenTransactionCreateView
      >(publicUrls.createTransactionForInvoice, adyenTransactionForInvoiceCreateRequest);
      set({ isLoading: false, adyenTransactionCreateView: response });
      return response;
    },
    handleBacsPayment: async (
      reference: string,
      bacsStateData: any,
      invoiceId: number
    ): Promise<{
      success: boolean;
      status: AdyenPaymentsResultCodes;
      pdf_url?: string;
    }> => {
      set({ isLoading: true });
      const response = await postRequestNoAuth<
        {
          transaction_ref: string;
          mandated_by: AdyenMandatedBy;
          payment_method_data: any;
          invoice_id: number;
        },
        BacsResponse
      >(publicUrls.handleBacsPayment, {
        transaction_ref: reference,
        mandated_by: AdyenMandatedBy.Self,
        payment_method_data: bacsStateData,
        invoice_id: invoiceId
      });
      set({ isLoading: false });
      return response;
    },
    getTransactionCreateResult: async (transactionRef: string, resultCode: string) => {
      set({ isLoading: true });
      const response = await getRequestNoAuth<ShowPaymentResultView>(
        publicUrls.getTransactionCreatResult.replace("{transactionRef}", transactionRef),
        { result_code: resultCode }
      );
      set({ isLoading: false });
      return response;
    }
  };

  return {
    isLoading: true,
    allowedPaymentMethods: {},
    availablePaymentMethods: {},
    clientPortalFunctions: {
      getAllowedPaymentMethods: cGetAllowedPaymentMethods,
      createAddPaymentMethodMandate: cCreateAddPaymentMethodMandate,
      getAvailablePaymentMethods: cGetAvailablePaymentMethods,
      doGiactValidation: cDoGiactValidation,
      handleBacsPayment: cHandleBacsPayment,
      createTransactionToken: cCreateTransactionToken,
      sendMandateCreationNotification: cSendMandateCreationNotification
    },
    partnerPortalFunctions: {
      createAddPaymentMethodMandate: pCreateAddPaymentMethodMandate,
      doGiactValidation: pDoGiactValidation,
      createTransactionToken: pCreateTransactionToken,
      sendMandateCreationNotification: pSendMandateCreationNotification,
      handleBacsPayment: pHandleBacsPayment
    },
    publicFunctions
  };
});
